import dayJS from 'dayjs'
import { defineStore } from 'pinia'
import { Platform, setCssVar } from 'quasar'
import { useServerSettingsStore } from 'src/CrudManager/Core/stores/useServerSettingsStore'
import { computed, ref, watch } from 'vue'
import { useUserStore } from './useUserStore'

// Define CompanyData interface
export interface CompanyData {
  id: string // Assuming UUID
  name: string
  menu_label: string | null
  menu_color: string | null
  header_color: string | null
  is_default: boolean
  is_active: boolean
}

interface AppSettings {
  theme: string | null
  debugMode: boolean
  forceMobileMenu: boolean
  showTooltips: boolean
  darkMode: boolean
  secondaryColor: string | null
  darkColor: string | null
  displayMenuLabel: boolean
  displayRightDrawer: boolean
  employeMode: {
    is_active: boolean
    orig_id: string
    employe: number | null
  }
  dateFormat: LabelValue
  gptSettings: {
    FormationManager: {
      display: boolean
    }
  }
  editorSettings: {
    tabPosition: 'top' | 'bottom'
    tabDisplay: 'full' | 'icons' | 'auto'
    autoSave: boolean
    confirmBeforeLeaving: boolean
    autoSaveDebounce: number // in seconds
  }
  listerSettings: {
    listMode: 'table' | 'simpleCards' | 'detailedCards'
    /** Per-model override for the lister view mode. Keyed by modelName. */
    modelListModes?: Record<string, 'table' | 'simpleCards' | 'detailedCards'>
  }
  companies: CompanyData[] // Added availableCompanies
  availableCompanies: CompanyData[]
  currentCompany: CompanyData | null
  companyId: CompanyData['id'] | null
}

export interface Theme {
  colors: Record<string, string>
}

export interface LabelValue {
  label: string
  value: string
}

export interface UserSetting {
  key: string
  value: string | number | boolean | null
}
export const useAppSettingsStore = defineStore(
  'AppSettings',
  () => {
    const serverSettingsStore = useServerSettingsStore()
    // Store state variables
    const theme = ref<Theme | null>(null)
    const debugMode = ref(false)
    const forceMobileMenu = ref(false)
    const showTooltips = ref(true)
    const darkMode = ref(false)

    /**
     * Toggle dark mode and apply/remove the `dark` class on the root element.
     */
    const toggleDarkMode = (value?: boolean): void => {
      darkMode.value = value !== undefined ? value : !darkMode.value
      applyDarkMode()
    }

    /**
     * Apply the current dark mode state to the DOM.
     */
    const applyDarkMode = (): void => {
      if (darkMode.value) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    }

    /**
     * Initialize dark mode from persisted state.
     * Should be called on app initialization.
     */
    const initDarkMode = (): void => {
      applyDarkMode()
    }
    const secondaryColor = ref<string | null>(null)
    const darkColor = ref<string | null>(null)
    const displayMenuLabel = ref(true)
    const displayRightDrawer = ref(false)
    const employeMode = ref<AppSettings['employeMode']>({ is_active: false, orig_id: null, employe: null })
    const gptSettings = ref<AppSettings['gptSettings']>({ FormationManager: { display: true } })
    const companies = ref<CompanyData[]>([]) // Added state for available companies
    const availableCompanies = computed(() => companies.value.filter((e) => e.is_active && e.id !== currentCompany.value?.id))

    const currentCompany = ref<CompanyData | null>(null)
    const editorSettings = ref({
      tabPosition: 'top',
      tabDisplay: 'auto',
      autoSave: true,
      confirmBeforeLeaving: true,
      autoSaveDebounce: 3, // 3 seconds default
    })

    const listerSettings = ref<AppSettings['listerSettings']>({
      listMode: 'simpleCards',
      modelListModes: {},
    })

    const sidebarSettings = ref<{ recentsCount: number }>({
      recentsCount: 3,
    })

    //sort by not currentCompany first

    const userSettings = ref<UserSetting[]>([])
    const flatCards = ref(Platform.is.mobile ? true : false)

    // getUserSettings and setUserSettings allows any part of the application to store its settings user a key identifier

    const dateFormat = ref<AppSettings['dateFormat']>({
      label: 'AAAA-MM-JJ',
      value: 'YYYY-MM-DD',
    })

    const userStore = useUserStore()
    // Methods
    const setTheme = () => {
      if (theme.value) {
        for (const [key, value] of Object.entries(theme.value.colors)) {
          setCssVar(key, value)
        }
      }
    }

    const switchUser = () => userStore.regenUserData()

    const employeModeId = computed<number | false>(() => (employeMode.value.is_active && employeMode.value.employe ? employeMode.value.employe : false))

    watch(theme, () => {
      if (theme.value) {
        setTheme()
      }
    })

    const formattedDate = computed((e: string | number | Date = '') => dayJS(e).format(dateFormat.value.value))
    const formattedDateTime = computed((e: string | number | Date = '') => dayJS(e).format(`${dateFormat.value.value} H:m:s`))

    const getUserSettings = (key: string, defaultSetting: string | number | boolean | null = null) => {
      let setting = userSettings.value.find((e) => e.key === key)
      if (!setting) {
        setting = setUserSettings(key, defaultSetting)
      }

      return computed({ get: () => setting.value, set: (value) => setUserSettings(key, value) })
    }

    const setUserSettings = (key: string, value: string | number | boolean | null) => {
      const index = userSettings.value.findIndex((e) => e.key === key)
      if (index > -1) {
        userSettings.value[index].value = value
      } else {
        userSettings.value.push({ key, value })
      }

      return userSettings.value.find((e) => e.key === key)
    }

    // Setter function for available companies
    const setAvailableCompanies = (c: CompanyData[]) => {
      companies.value = c

      if (!currentCompany.value || !companies.value.find((comp) => comp.id === currentCompany.value?.id)) {
        currentCompany.value = companies.value.find((comp) => comp.is_default) ?? companies.value[0]
      }
    }

    /**
     * The active company scope — persisted, and stamped on every request as
     * X-Company-ID. Only a deliberate user action sets it (the sidebar's
     * « Changer de compagnie »); it is never derived from the data on screen.
     * Deliberately takes a company rather than an id: an id absent from
     * `companies` would null the scope, which drops the header entirely and
     * silently widens every query.
     */
    const setCurrentCompany = async (company: CompanyData) => {
      currentCompany.value = company
      await serverSettingsStore.fetchSettings()
    }

    return {
      gptSettings,
      formattedDate,
      formattedDateTime,
      dateFormat,
      setTheme,
      switchUser,
      employeModeId,
      debugMode,
      currentCompany,
      setCurrentCompany,
      employeMode,
      forceMobileMenu,
      showTooltips,
      darkMode,
      toggleDarkMode,
      initDarkMode,
      secondaryColor,
      darkColor,
      displayMenuLabel,
      displayRightDrawer,
      theme,
      getUserSettings,
      setUserSettings,
      editorSettings,
      listerSettings,
      sidebarSettings,
      flatCards,
      companies,
      availableCompanies, // Export availableCompanies
      setAvailableCompanies, // Export setter function
    }
  },
  { persist: true },
)
